import { batch } from 'react-redux';

// project imports
import gestopAPI from 'utils/gestopAPI';
import { dispatch } from 'store/index';
import { errorProcessor } from 'utils/processors';
import { taskActions } from './slice';
import capabilities from './capabilities';

export const ApiTasksGetCollection = async ({
    filters = [],
    orderBy = [],
    groupBy = [],
    page = 1,
    limit = 10,
    includeSafeDeleted = false,
    showLoader = true,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (showLoader && capabilities.app.includes('loader')) {
        dispatch(taskActions.startLoading());
    }

    await gestopAPI
        .post('workmgmt/tasks/list', { filters, orderBy, groupBy, page, limit, includeSafeDeleted }, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(taskActions.setList(response?.data?.list));
                    dispatch(taskActions.setCount(response?.data?.count));
                }
                if (showLoader && capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data?.list);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (showLoader && capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(taskActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTasksCountCollection = async ({
    filters = [],
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    await gestopAPI
        .post('workmgmt/tasks/count', { filters, includeSafeDeleted }, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data?.count);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiTasksPostCollection = async ({
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(taskActions.startLoading());
    }

    await gestopAPI
        .post('workmgmt/tasks', data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(taskActions.insertItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(taskActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTasksDeleteCollection = async ({
    ids = [],
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(taskActions.startLoading());
    }

    await gestopAPI
        .post('workmgmt/tasks/safedelete', ids, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(taskActions.deleteItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(taskActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTasksPutItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(taskActions.startLoading());
    }

    await gestopAPI
        .put(`workmgmt/tasks/${id}`, data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(taskActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(taskActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTasksPatchItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(taskActions.startLoading());
    }

    await gestopAPI
        .patch(`workmgmt/tasks/${id}`, data, {
            ...config,
            headers: { ...config?.headers, 'Content-Type': 'application/merge-patch+json' } // Specific content type for PATCH requests
        })
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(taskActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(taskActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTasksDeleteItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, updateStore = true, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(taskActions.startLoading());
    }

    await gestopAPI
        .post(`workmgmt/tasks/safedelete/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(taskActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(taskActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTasksRestoreDeletedItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(taskActions.startLoading());
    }

    await gestopAPI
        .post(`workmgmt/tasks/restoresafedeleted/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(taskActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(taskActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTasksApplyTransItem = async ({
    id,
    transition = '',
    data = {},
    successCallback,
    errorCallback,
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(taskActions.startLoading());
    }

    await gestopAPI
        .post(`workmgmt/tasks/applytrans/${transition}/${id}`, data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(taskActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(taskActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTasksGetItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(taskActions.startLoading());
    }

    await gestopAPI
        .get(`workmgmt/tasks/${id}`, config)
        .then((response) =>
            batch(() => {
                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(taskActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(taskActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};
